import type { ReaderPage } from "@/mocks/readerPages";
import type { ReadingMode } from "./ReaderToolbar";

interface Props {
  pages: ReaderPage[];
  mode: ReadingMode;
  pageIndex: number;
  night: boolean;
  onPrev: () => void;
  onNext: () => void;
  maxPage: number;
  onScrollModeProgress: (pct: number) => void;
}

export default function ReaderCanvas({
  pages,
  mode,
  pageIndex,
  night,
  onPrev,
  onNext,
  maxPage,
  onScrollModeProgress,
}: Props) {
  if (mode === "scroll") {
    return (
      <div
        className={`flex flex-col items-center gap-4 py-6 md:gap-6 ${
          night ? "bg-background-950" : "bg-background-100/70"
        }`}
      >
        <div className="flex flex-col items-center gap-3">
          {pages.map((p, i) => (
            <img
              key={p.seq}
              src={p.src}
              alt={`滚动阅读第${i + 1}页`}
              className="w-full max-w-3xl rounded-sm object-contain shadow-sm"
            />
          ))}
        </div>
      </div>
    );
  }

  const left = pages[pageIndex];
  const right = pages[pageIndex + 1];

  return (
    <div
      className={`relative flex min-h-[70vh] items-center justify-center gap-3 px-3 py-6 md:gap-6 md:px-6 ${
        night ? "bg-background-950" : "bg-background-100/70"
      }`}
    >
      {pageIndex > 0 && (
        <button
          type="button"
          aria-label="上一页"
          onClick={onPrev}
          className={`absolute left-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full shadow-sm transition-colors md:left-5 md:h-11 md:w-11 ${
            night
              ? "bg-background-900 text-foreground-200 hover:bg-background-800"
              : "bg-background-50 text-foreground-700 hover:bg-primary-500 hover:text-background-50"
          }`}
        >
          <i className="ri-arrow-left-s-line text-xl md:text-2xl"></i>
        </button>
      )}

      <div className="flex items-start justify-center gap-3 md:gap-5">
        <img
          src={left.src}
          alt={left.label}
          className="h-[62vh] md:h-[78vh] w-auto rounded-sm object-contain shadow-sm"
        />
        {right && (
          <img
            src={right.src}
            alt={right.label}
            className="hidden h-[78vh] w-auto rounded-sm object-contain shadow-sm sm:block"
          />
        )}
      </div>

      {pageIndex < maxPage && (
        <button
          type="button"
          aria-label="下一页"
          onClick={onNext}
          className={`absolute right-2 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full shadow-sm transition-colors md:right-5 md:h-11 md:w-11 ${
            night
              ? "bg-background-900 text-foreground-200 hover:bg-background-800"
              : "bg-background-50 text-foreground-700 hover:bg-primary-500 hover:text-background-50"
          }`}
        >
          <i className="ri-arrow-right-s-line text-xl md:text-2xl"></i>
        </button>
      )}

      <span
        className={`absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-xs ${
          night ? "bg-background-900 text-foreground-300" : "bg-background-50 text-foreground-600"
        }`}
      >
        第 {pageIndex + 1} / {pages.length} 页
      </span>
    </div>
  );
}